/**
 * Firestore Service - Abstraction layer for all Firestore operations
 * §7 CLAUDE.md: Les composants n'appellent jamais Firebase directement
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  runTransaction,
  QueryConstraint,
  DocumentData,
  QuerySnapshot,
  QueryDocumentSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase/config';
import type { GenerationRecipe } from '../features/canvas/types/recipe';
import { createOperationId } from '../utils/operationId';

// Re-export for typing
export { serverTimestamp, arrayUnion, arrayRemove };
export type { Unsubscribe, QuerySnapshot, DocumentData, QueryConstraint };

// ============================================================================
// GENERIC CRUD OPERATIONS
// ============================================================================

export const getDocument = async <T>(
  collectionName: string,
  docId: string
): Promise<T | null> => {
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? ({ id: docSnap.id, ...docSnap.data() } as T) : null;
};

export const updateDocument = async (
  collectionName: string,
  docId: string,
  data: Partial<DocumentData>
): Promise<void> => {
  const docRef = doc(db, collectionName, docId);
  await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
};

export const deleteDocument = async (
  collectionName: string,
  docId: string
): Promise<void> => {
  const docRef = doc(db, collectionName, docId);
  await deleteDoc(docRef);
};

const addDocument = async <T extends DocumentData>(
  collectionName: string,
  data: T
): Promise<string> => {
  const colRef = collection(db, collectionName);
  const docRef = await addDoc(colRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

// ============================================================================
// QUERY OPERATIONS
// ============================================================================

export const queryDocuments = async <T>(
  collectionName: string,
  constraints: QueryConstraint[] = []
): Promise<T[]> => {
  const colRef = collection(db, collectionName);
  const q = query(colRef, ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as T));
};

// ============================================================================
// REAL-TIME LISTENERS
// ============================================================================

export const subscribeToDocument = <T>(
  collectionName: string,
  docId: string,
  onData: (data: T | null, hasPendingWrites?: boolean) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const docRef = doc(db, collectionName, docId);
  return onSnapshot(
    docRef,
    (docSnap) => {
      onData(
        docSnap.exists() ? ({ id: docSnap.id, ...docSnap.data() } as T) : null,
        docSnap.metadata.hasPendingWrites
      );
    },
    onError
  );
};

export const subscribeToCollection = <T>(
  collectionName: string,
  constraints: QueryConstraint[],
  onData: (data: T[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const colRef = collection(db, collectionName);
  const q = query(colRef, ...constraints);
  return onSnapshot(
    q,
    (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as T));
      onData(data);
    },
    onError
  );
};

// ============================================================================
// QUERY HELPERS (re-export for convenience)
// ============================================================================

export { where, orderBy, limit, startAfter };

// ============================================================================
// SPECIFIC COLLECTION OPERATIONS
// ============================================================================

// Projects
export const createProject = (data: DocumentData) => addDocument('projects', data);
export const updateProject = (id: string, data: Partial<DocumentData>) =>
  updateDocument('projects', id, data);
export const deleteProject = (id: string) => deleteDocument('projects', id);

/**
 * Met à jour les poids de randomisation de plusieurs prompts sauvegardés
 * dans un projet, en une seule transaction.
 */
export const updateSavedPromptWeights = async (
  projectId: string,
  weights: Record<string, number>
): Promise<void> => {
  const projectRef = doc(db, 'projects', projectId);

  await runTransaction(db, async (transaction) => {
    const projectSnap = await transaction.get(projectRef);
    if (!projectSnap.exists()) {
      throw new Error(`Project ${projectId} not found`);
    }

    const data = projectSnap.data();
    const savedPrompts = (data.savedPrompts || []) as Array<{
      promptId: string;
      weight?: number;
      [key: string]: unknown;
    }>;

    const updatedPrompts = savedPrompts.map((prompt) =>
      weights[prompt.promptId] !== undefined
        ? { ...prompt, weight: weights[prompt.promptId] }
        : prompt
    );

    transaction.update(projectRef, {
      savedPrompts: updatedPrompts,
      updatedAt: serverTimestamp(),
    });
  });
};

/**
 * Réordonne les prompts sauvegardés d'UNE catégorie d'un projet (feature
 * « Reroll settings » — drag & drop du panneau Manage prompts), en une seule
 * transaction (même pattern que updateSavedPromptWeights). `orderedPromptIds`
 * = nouvel ordre des promptIds de la catégorie ; la reconstruction parcourt
 * le tableau ORIGINAL et remplace chaque entrée de la catégorie par la
 * suivante de la liste réordonnée — les positions des autres catégories sont
 * préservées. Une entrée de la catégorie absente de la liste réordonnée
 * (ajout concurrent) conserve sa position relative, à la fin du groupe.
 */
export const reorderSavedPrompts = async (
  projectId: string,
  category: string,
  orderedPromptIds: string[]
): Promise<void> => {
  const projectRef = doc(db, 'projects', projectId);

  await runTransaction(db, async (transaction) => {
    const projectSnap = await transaction.get(projectRef);
    if (!projectSnap.exists()) {
      throw new Error(`Project ${projectId} not found`);
    }

    const data = projectSnap.data();
    const savedPrompts = (data.savedPrompts || []) as Array<{
      promptId: string;
      category: string;
      [key: string]: unknown;
    }>;

    // Entrées de la catégorie dans leur nouvel ordre, puis celles absentes de
    // la liste réordonnée (staleness : ajout entre le drag et le commit).
    const byId = new Map(
      savedPrompts.filter((p) => p.category === category).map((p) => [p.promptId, p])
    );
    const reorderedCategory = orderedPromptIds
      .map((id) => byId.get(id))
      .filter((p): p is NonNullable<typeof p> => p !== undefined);
    for (const entry of byId.values()) {
      if (!orderedPromptIds.includes(entry.promptId)) {
        reorderedCategory.push(entry);
      }
    }

    let categoryCursor = 0;
    const updatedPrompts = savedPrompts.map((prompt) =>
      prompt.category === category ? reorderedCategory[categoryCursor++] : prompt
    );

    transaction.update(projectRef, {
      savedPrompts: updatedPrompts,
      updatedAt: serverTimestamp(),
    });
  });
};

// Prompts
export const getPrompts = (constraints: QueryConstraint[] = []) =>
  queryDocuments('prompts', constraints);

export const createPrompt = (data: DocumentData) => addDocument('prompts', data);
export const updatePrompt = (id: string, data: Partial<DocumentData>) =>
  updateDocument('prompts', id, data);
export const deletePrompt = (id: string) => deleteDocument('prompts', id);

// ============================================================================
// PROMPT HISTORY — collection append-only scopée par userId. Deux sources
// d'écriture :
//   - SERVEUR (persistance generateImage, issue #73) : texte + champs image
//     optionnels imageStoragePath/thumbnailStoragePath/seed/model/width/
//     height — cf. AGENTS.md §2.1 ;
//   - CLIENT (mode « Prompt » du GeneratePanel) : entrées texte-only
//     (userId/prompt/projectId/createdAt), prompts composés sans génération
//     d'image, aucun quota consommé.
// Lecture client : flux Prompt du GeneratePanel (PromptPreview, textes),
// flux des runs et popin d'historique (GenerationPreview / HistoryModal)
// — vignettes des images générées (entrées avec image seules).
// ============================================================================

export interface PromptHistoryEntry {
  id?: string;
  userId: string;
  prompt: string;
  createdAt?: unknown;
  // Champs image (issue #73) — écrits par le SERVEUR uniquement ; absents sur
  // les entrées texte-only. URLs résolues via storage.service.getFileUrl.
  imageStoragePath?: string;
  thumbnailStoragePath?: string;
  seed?: number;
  model?: string;
  width?: number;
  height?: number;
  /** 'video' pour les entrées ltx25 : le MP4 est tracé dans imageStoragePath
      et sa vignette (frame JPEG extraite par ffmpeg au persist, issue #79)
      dans thumbnailStoragePath — absente des entrées antérieures à la
      feature (pas de backfill, repli skeleton côté UI). Absent = image. */
  mediaType?: 'image' | 'video';
  /** Vidéo uniquement : durée (s), fps (24 fixe) et format demandés. */
  duration?: number;
  fps?: number;
  aspectRatio?: string;
  /** Vidéo uniquement : piste audio LTX demandée (false = MP4 sans son). */
  audio?: boolean;
  /** Vidéo uniquement : palier HD « 720p » demandé (absent/false = SD). */
  hd?: boolean;
  /** Vidéo I2V : chemin Storage de l'image source, reconduit au Generate again. */
  sourceImagePath?: string;
  /** ms — temps de génération pur mesuré par RunPod (refonte multi-modèles). */
  executionTime?: number;
  /** LoRA résolu serveur après validation du `characterId` (refonte
      multi-modèles) — absents quand aucun personnage n'est associé. */
  loraName?: string;
  /** Deux LoRA maximum, dans l'ordre de la chaîne — spike duo Krea 2. */
  loraNames?: string[];
  loraStrength?: number;
  // Projet d'origine de la génération (revue PO) — absent sur les entrées
  // antérieures au scoping par projet.
  projectId?: string;
  /** Recette « Iterate » (feature) : snapshot structuré du canvas à
      l'enqueue (version, blocs, wires, prompts embarquées, modèle, réglages,
      seed). LEGACY INLINE (issue #83) : n'est plus écrit sur l'entrée — la
      recette vit dans generationRecipes/{entryId} et est chargée à la
      demande (resolveEntryRecipe) ; ce champ ne subsiste que sur les
      entrées antérieures à la sortie des recettes des listeners. */
  recipe?: GenerationRecipe;
  /** true = une recette « Iterate » existe dans generationRecipes/{id}
      (issue #83) — écrit par le SERVEUR uniquement au persist. Permet de
      savoir qu'une lecture à la demande vaut le coup, sans transporter la
      recette dans les listeners. */
  hasRecipe?: boolean;
}

// Abonnement temps réel à l'historique d'un utilisateur, plus récent d'abord.
// projectId (optionnel, revue PO) : ne remonte que les entrées générées dans
// ce projet — les entrées antérieures au champ n'apparaissent nulle part.
export const subscribePromptHistory = (
  userId: string,
  onData: (entries: PromptHistoryEntry[]) => void,
  onError?: (error: Error) => void,
  max: number = 60,
  projectId?: string
) =>
  subscribeToCollection<PromptHistoryEntry>(
    'promptHistory',
    [
      where('userId', '==', userId),
      ...(projectId ? [where('projectId', '==', projectId)] : []),
      orderBy('createdAt', 'desc'),
      limit(max),
    ],
    onData,
    onError
  );

// Page one-shot de l'historique d'un projet, par CURSEUR (refonte perf
// /content) : contrairement à la fenêtre limit() grandissante qui
// ré-abonnait le listener et re-synchronisait toute la fenêtre à chaque
// page, chaque page n'est lue qu'UNE fois (getDocs + startAfter). Le curseur
// est le snapshot du dernier doc de la page précédente — il reste valide
// même si le doc est supprimé entre-temps, contrairement à un curseur sur la
// valeur createdAt. Même where + orderBy (même index composite).
export const fetchProjectMediaPage = async (
  userId: string,
  projectId: string,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
  pageSize: number
): Promise<{
  entries: PromptHistoryEntry[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}> => {
  const colRef = collection(db, 'promptHistory');
  const q = query(
    colRef,
    where('userId', '==', userId),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(pageSize)
  );
  const snapshot = await getDocs(q);
  const entries = snapshot.docs.map(
    (d) => ({ id: d.id, ...d.data() }) as PromptHistoryEntry
  );
  return {
    entries,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] ?? null,
    hasMore: entries.length === pageSize,
  };
};

const createTextRun = httpsCallable(functions, 'createTextRun');

// Écriture SERVEUR d'une entrée texte-only (mode « Prompt » du GeneratePanel)
// : prompt composée sans génération — aucun appel GPU, aucun quota consommé.
// La callable crée l'historique et le compteur fiable dans UNE transaction,
// avec idempotence par operationId ; pas de recette « Iterate » sur ces entrées.
export const createPromptHistoryEntry = async (
  userId: string,
  prompt: string,
  projectId: string
): Promise<string> => {
  const operationId = createOperationId();
  await createTextRun({ operationId, prompt, projectId });
  return operationId;
};

// Lecture PONCTUELLE de TOUTES les entrées média (image ou vidéo) d'un
// projet — bouton « Download all » de la page Content : contrairement à
// l'abonnement de la galerie (fenêtre limit() paginée au scroll), ici la
// liste doit être COMPLÈTE, sans limite. Même where + orderBy (même index
// composite). Une requête getDocs au service d'une action explicite de
// l'utilisateur — pas un comptage (A2/A3 sans rapport).
export const fetchAllProjectMediaHistory = async (
  userId: string,
  projectId: string
): Promise<PromptHistoryEntry[]> => {
  const entries = await queryDocuments<PromptHistoryEntry>('promptHistory', [
    where('userId', '==', userId),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc'),
  ]);
  // Mêmes critères que la galerie : entrées avec un fichier média (les
  // entrées texte-only n'ont rien à télécharger).
  return entries.filter(
    (entry) => entry.thumbnailStoragePath || entry.imageStoragePath
  );
};

// ============================================================================
// GENERATION JOBS — état durable des générations RunPod /run (issue #73).
// Écrits par le serveur uniquement (Admin SDK) : status IN_PROGRESS →
// COMPLETED/FAILED + phase détaillée, runpodJobId et résultat persisté.
// Lecture admin globale (subscribeAllGenerationJobs — page admin Logs).
// ============================================================================

export interface GenerationJobEntry {
  /** docId = operationId UUID créé à l'enqueue (requestId serveur pour les jobs legacy). */
  id?: string;
  uid: string;
  prompt: string;
  model?: string;
  /** Endpoint RunPod ciblé (multi-endpoints) — absent sur les jobs
      antérieurs à la refonte multi-endpoints. */
  endpointId?: string;
  /** 'video' pour les jobs ltx25 (duration/aspectRatio au lieu de
      width/height). Absent = image. */
  mediaType?: 'image' | 'video';
  duration?: number;
  aspectRatio?: string;
  /** Vidéo uniquement : piste audio demandée (false = MP4 sans son). */
  audio?: boolean;
  /** Vidéo uniquement : palier HD « 720p » demandé (absent/false = SD). */
  hd?: boolean;
  width?: number;
  height?: number;
  seed?: number;
  /** LoRA résolu serveur après validation du `characterId` — absents quand
      aucun personnage n'est associé. */
  loraName?: string;
  loraNames?: string[];
  loraStrength?: number;
  /** Character LoRA (krea2) ou dataset builder (Krea 2 i2i) — personnage lié. */
  characterId?: string;
  characterIds?: string[];
  /** Moteur d'édition i2i du dataset builder ('krea2-i2i'). */
  engine?: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  /** Phase interne détaillée (soumission, file RunPod, finalisation). */
  phase?: string;
  runpodJobId?: string;
  runpodStatus?: string;
  imageStoragePath?: string;
  thumbnailStoragePath?: string | null;
  errorCode?: string;
  failureKind?: 'high-volume' | 'content-policy' | 'moderation-unavailable' | 'quota-exceeded';
  error?: string;
  /** ms — temps de génération pur mesuré par RunPod. */
  executionTime?: number;
  /** ms — temps passé en file d'attente RunPod (cold start inclus). */
  delayTime?: number;
  projectId?: string;
  createdAt?: unknown;
}

// Abonnement temps réel aux jobs de génération de TOUS les utilisateurs,
// plus récent d'abord — page admin Logs (AdminLogsPage) et compteurs de
// l'onglet Beta users (AdminBetaUsers). Les règles autorisent la lecture
// admin de tous les docs generationJobs ; sans filtre where, aucun index
// composite n'est requis. max = 0 → pas de limite (⚠️ les écrans admin
// passent par une fenêtre bornée extensible — useAllGenerationJobs,
// issue #82 ; max = 0 reste disponible pour un besoin ponctuel).
export const subscribeAllGenerationJobs = (
  onData: (entries: GenerationJobEntry[]) => void,
  onError?: (error: Error) => void,
  max: number = 100
) =>
  subscribeToCollection<GenerationJobEntry>(
    'generationJobs',
    [orderBy('createdAt', 'desc'), ...(max > 0 ? [limit(max)] : [])],
    onData,
    onError
  );

// Lecture directe des jobs suivis par la session : docId = operationId = run.id.
// Les règles owner existantes suffisent, sans requête composite ni nouvel index.
export const fetchGenerationJobsByIds = async (
  jobIds: string[]
): Promise<GenerationJobEntry[]> => {
  const jobs = await Promise.all(
    [...new Set(jobIds)].map((jobId) =>
      getDocument<GenerationJobEntry>('generationJobs', jobId)
    )
  );
  return jobs.filter((job): job is GenerationJobEntry => Boolean(job));
};

// Efface les logs de génération d'un utilisateur. projectId (optionnel) :
// seuls les jobs du projet courant sont supprimés. La suppression owner est
// autorisée par les règles (create/update restent réservés au serveur ; un
// admin ne peut PAS effacer les jobs des autres utilisateurs).
export const clearGenerationJobs = async (
  userId: string,
  projectId?: string
): Promise<void> => {
  const jobs = await queryDocuments<GenerationJobEntry>('generationJobs', [
    where('uid', '==', userId),
    ...(projectId ? [where('projectId', '==', projectId)] : []),
  ]);
  await Promise.all(
    jobs
      .filter((j) => j.id)
      .map((j) => deleteDocument('generationJobs', j.id as string))
  );
};

// ============================================================================
// ADVANCED QUERY OPERATIONS (for pagination)
// ============================================================================

export interface QueryResult<T> {
  data: T[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  isEmpty: boolean;
}

/**
 * Subscribe to a collection with raw snapshot access (for pagination cursors)
 */
export const subscribeToCollectionWithSnapshot = <T>(
  collectionName: string,
  constraints: QueryConstraint[],
  onData: (data: T[], snapshot: QuerySnapshot<DocumentData>) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const colRef = collection(db, collectionName);
  const q = query(colRef, ...constraints);
  return onSnapshot(
    q,
    (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as T));
      onData(data, snapshot);
    },
    onError
  );
};

/**
 * Query documents with pagination support (returns last doc for cursor)
 */
export const queryDocumentsWithCursor = async <T>(
  collectionName: string,
  constraints: QueryConstraint[] = []
): Promise<QueryResult<T>> => {
  const colRef = collection(db, collectionName);
  const q = query(colRef, ...constraints);
  const snapshot = await getDocs(q);

  return {
    data: snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as T)),
    lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
    isEmpty: snapshot.empty
  };
};

/**
 * Get raw collection reference for advanced operations
 */
export const getCollectionRef = (collectionName: string) => collection(db, collectionName);

/**
 * Get raw document reference
 */

